/**
 * Keyboard control of the map.
 *
 * A canvas is one element, so none of the items can be tabbed to and none of
 * them can carry a focus ring.  The substitute is a **cursor**: the canvas
 * takes focus once, and then the map moves under the keyboard and one item at
 * a time is marked as current.  Everything a pointer can do has a key:
 *
 * | key                    | action                                    |
 * | ---------------------- | ----------------------------------------- |
 * | arrows                 | pan (hold Shift to pan four times as far) |
 * | `+` / `-`              | zoom about the centre                     |
 * | `0`                    | fit the map to the canvas                 |
 * | `]` / `[`              | next / previous item by weight            |
 * | `Home` / `End`         | heaviest / lightest item                  |
 * | `Enter` or `Space`     | select the current item                   |
 * | `Escape`               | clear the selection and the cursor        |
 *
 * The cursor is announced through the same `select` event a click produces, so
 * an application that shows a details panel needs no extra wiring to be
 * keyboard accessible.
 */

export interface KeyboardHandlers {
  onPan(dx: number, dy: number): void;
  onZoom(factor: number): void;
  onFit(): void;
  /** Move the cursor by `delta` ranks in the weight order. */
  onCursorStep(delta: number): void;
  /** `-1` for the heaviest item, `1` for the lightest. */
  onCursorEnd(which: -1 | 1): void;
  onActivate(): void;
  onEscape(): void;
}

const PAN_STEP = 48;
const PAN_STEP_FAST = 192;
const ZOOM_STEP = 1.25;

export class KeyboardController {
  private readonly canvas: HTMLCanvasElement;

  private readonly handlers: KeyboardHandlers;

  private readonly addedTabIndex: boolean;

  private readonly addedRole: boolean;

  constructor(canvas: HTMLCanvasElement, handlers: KeyboardHandlers) {
    this.canvas = canvas;
    this.handlers = handlers;
    // A canvas is not focusable by default. Only add the attribute if the host
    // has not set one, and remove it again on destroy so we leave the element
    // as we found it.
    this.addedTabIndex = !canvas.hasAttribute('tabindex');
    if (this.addedTabIndex) canvas.setAttribute('tabindex', '0');
    this.addedRole = !canvas.hasAttribute('role');
    if (this.addedRole) canvas.setAttribute('role', 'application');
    canvas.addEventListener('keydown', this.onKeyDown);
  }

  private onKeyDown = (event: KeyboardEvent): void => {
    // Leave browser and screen-reader shortcuts alone.
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const step = event.shiftKey ? PAN_STEP_FAST : PAN_STEP;
    let handled = true;
    switch (event.key) {
      case 'ArrowLeft':
        this.handlers.onPan(step, 0);
        break;
      case 'ArrowRight':
        this.handlers.onPan(-step, 0);
        break;
      case 'ArrowUp':
        this.handlers.onPan(0, step);
        break;
      case 'ArrowDown':
        this.handlers.onPan(0, -step);
        break;
      case '+':
      case '=':
        this.handlers.onZoom(ZOOM_STEP);
        break;
      case '-':
      case '_':
        this.handlers.onZoom(1 / ZOOM_STEP);
        break;
      case '0':
        this.handlers.onFit();
        break;
      case ']':
        this.handlers.onCursorStep(1);
        break;
      case '[':
        this.handlers.onCursorStep(-1);
        break;
      case 'Home':
        this.handlers.onCursorEnd(-1);
        break;
      case 'End':
        this.handlers.onCursorEnd(1);
        break;
      case 'Enter':
      case ' ':
        this.handlers.onActivate();
        break;
      case 'Escape':
        this.handlers.onEscape();
        break;
      default:
        handled = false;
    }
    if (handled) event.preventDefault();
  };

  destroy(): void {
    this.canvas.removeEventListener('keydown', this.onKeyDown);
    if (this.addedTabIndex) this.canvas.removeAttribute('tabindex');
    if (this.addedRole) this.canvas.removeAttribute('role');
  }
}
